import { keepPreviousData } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';

import type {
  TopResult,
  TopResultIdentifier,
} from '#/api/generated/fINRAMarketDataAPI.schemas';
import { useGetTopResult } from '#/api/generated/search/search';
import { formatDetailExchangeLabel } from '#/components/detail/exchangeLabel';
import { TickerPriceSummary } from '#/components/detail/TickerPriceSummary';
import { ArticleCard } from '#/components/ui/ArticleCard';
import { CategoryCard } from '#/components/ui/CategoryCard';
import { SectionHeader } from '#/components/ui/SectionHeader';
import {
  Skeleton,
  SkeletonRegion,
  SkeletonText,
} from '#/components/ui/Skeleton';
import { Ticker } from '#/components/ui/Ticker';
import { isSuccessResponse } from '#/lib/api-client';
import { cn } from '#/utils/cn';
import {
  getEquityETFPriceChangeDisplay,
  getMutualFundPriceChangeDisplay,
} from '#/utils/priceChange';

const INVESTMENT_TYPE_LABELS = {
  equity: 'Equity',
  etf: 'ETF',
  mutual_fund: 'Mutual Fund',
} as const;

const INVESTMENT_TYPE_CATEGORY_SLUGS = {
  equity: 'equities',
  etf: 'etfs',
  mutual_fund: 'mutual-funds',
} as const;

interface TopResultCardProps {
  resultIdentifier: TopResultIdentifier;
  isPending?: boolean;
}

export function TopResultCard({
  resultIdentifier,
  isPending,
}: TopResultCardProps) {
  const topResultQuery = useGetTopResult(
    {
      investment_type: resultIdentifier.investment_type,
      ticker: resultIdentifier.ticker ?? '',
    },
    {
      query: {
        enabled: Boolean(resultIdentifier.ticker),
        placeholderData: keepPreviousData,
        refetchOnReconnect: false,
        refetchOnWindowFocus: false,
      },
    },
  );

  const result = isSuccessResponse(topResultQuery.data)
    ? topResultQuery.data.data
    : undefined;
  const resolvedResult =
    result?.ticker === resultIdentifier.ticker ? result : undefined;
  const title = result?.ticker
    ? (resultIdentifier.name ?? result.ticker)
    : (resultIdentifier.name ?? resultIdentifier.ticker ?? 'Top result');
  const detailParams =
    resultIdentifier.ticker != null
      ? {
          category:
            INVESTMENT_TYPE_CATEGORY_SLUGS[resultIdentifier.investment_type],
          id: resultIdentifier.ticker,
        }
      : null;
  const priceChangeDisplay = getPriceChangeDisplay(resolvedResult);
  const relatedCards = getRelatedCards(resolvedResult);
  const isDetailLoading =
    Boolean(resultIdentifier.ticker) &&
    (topResultQuery.isPending ||
      (topResultQuery.isFetching && resolvedResult == null));
  const showLoadingState = isPending;

  return (
    <section className="flow flow-32 flow-inner-24">
      <SectionHeader title="Top Result" />

      <div className="grid gap-20">
        <SkeletonRegion
          label={`Loading top result details for ${title}`}
          className="grid gap-20"
        >
          <article
            aria-busy={isDetailLoading}
            className={cn(
              'border-blue-20 bg-gray-10 rounded border p-24 shadow-(--shadow-card) transition-opacity',
              detailParams && 'group/top-result-card relative',
              showLoadingState ? 'opacity-50' : '',
            )}
          >
            <div className="flex flex-col gap-20">
              <div className="flex flex-col gap-12">
                <p className="text-link-fg text-static-12 font-semibold">
                  {INVESTMENT_TYPE_LABELS[resultIdentifier.investment_type]}
                </p>

                <div className="flex flex-col gap-8">
                  <h3 className="text-fg-header text-fluid-subheader leading-tight font-semibold">
                    {detailParams ? (
                      <Link
                        to="/$category/$id"
                        params={detailParams}
                        className="text-fg-strong focus-visible:outline-button-primary-bg no-underline focus-visible:outline-2 focus-visible:outline-offset-2"
                      >
                        {title}
                        <span
                          aria-hidden="true"
                          className="after:absolute after:inset-0 after:block after:content-['']"
                        />
                      </Link>
                    ) : (
                      title
                    )}
                  </h3>

                  <div className="flex flex-wrap items-center gap-8">
                    {resultIdentifier.ticker ? (
                      <Ticker symbol={resultIdentifier.ticker} size="sm" />
                    ) : null}

                    {resolvedResult?.exchange ? (
                      <span className="text-static-12 text-fg-muted">
                        on {formatDetailExchangeLabel(resolvedResult.exchange)}
                      </span>
                    ) : isDetailLoading ? (
                      <Skeleton className="h-16 w-80" />
                    ) : null}
                  </div>
                </div>
              </div>

              {resolvedResult?.overview ? (
                <p className="text-static-14 text-fg line-clamp-3 leading-relaxed">
                  {resolvedResult.overview}
                </p>
              ) : isDetailLoading ? (
                <SkeletonText lines={3} className="pt-4" />
              ) : null}

              <div className="flex flex-col gap-8">
                {isDetailLoading ? (
                  <TopResultPriceSummarySkeleton />
                ) : (
                  <TickerPriceSummary
                    price={resolvedResult?.price}
                    percentChange={priceChangeDisplay.percentChange}
                    valueChange={priceChangeDisplay.valueChange}
                    valueChangeSuffix={
                      priceChangeDisplay.periodLabel
                        ? ` ${priceChangeDisplay.periodLabel}`
                        : ''
                    }
                    showBorder={false}
                  />
                )}
              </div>
            </div>
          </article>

          {isDetailLoading ? (
            <div className="flex flex-wrap gap-20">
              {getRelatedCardSkeletons(resultIdentifier.investment_type).map(
                (card) => (
                  <div
                    key={card.key}
                    className={getRelatedCardItemClass(
                      resultIdentifier.investment_type,
                    )}
                  >
                    {card}
                  </div>
                ),
              )}
            </div>
          ) : relatedCards.length > 0 ? (
            <div className="flex flex-wrap gap-20">
              {relatedCards.map((card) => (
                <div
                  key={card.key}
                  className={getRelatedCardItemClass(
                    resultIdentifier.investment_type,
                  )}
                >
                  {card}
                </div>
              ))}
            </div>
          ) : null}
        </SkeletonRegion>
      </div>
    </section>
  );
}

function TopResultPriceSummarySkeleton() {
  return (
    <div className="flex flex-wrap items-center gap-8 px-16 py-8 sm:shrink-0">
      <Skeleton className="h-28 w-128" />
      <Skeleton className="h-28 w-88 rounded px-8 py-4" />
      <Skeleton className="h-20 w-96" />
    </div>
  );
}

function RelatedArticleCardSkeleton() {
  return (
    <article className="border-blue-20 bg-gray-10 flex h-full flex-col rounded border p-24 shadow-(--shadow-card)">
      <div className="flex items-start justify-between gap-12">
        <div className="flow flow-12 flex-1">
          <Skeleton className="h-14 w-56" />
          <SkeletonText lines={2} />
        </div>
        <Skeleton className="size-32 rounded-full" />
      </div>
      <SkeletonText lines={3} className="mt-8" />
      <Skeleton className="mt-16 h-18 w-112" />
    </article>
  );
}

function RelatedCategoryCardSkeleton() {
  return (
    <article className="border-blue-20 bg-gray-10 flex h-full min-h-160 w-full flex-col rounded border p-24 shadow-(--shadow-card)">
      <div className="flex items-start justify-between gap-16">
        <Skeleton className="h-14 w-112" />
        <Skeleton className="size-32 rounded-full" />
      </div>
      <div className="mt-auto flex min-w-0 flex-col gap-8">
        <SkeletonText lines={2} />
      </div>
    </article>
  );
}

function getRelatedCardSkeletons(
  investmentType: TopResultIdentifier['investment_type'],
) {
  if (investmentType === 'equity') {
    return [
      <RelatedArticleCardSkeleton key="article" />,
      <RelatedCategoryCardSkeleton key="industry" />,
      <RelatedCategoryCardSkeleton key="market-cap" />,
    ];
  }

  return [
    <RelatedArticleCardSkeleton key="article" />,
    <RelatedCategoryCardSkeleton key="fund-category" />,
  ];
}

function getRelatedCardItemClass(
  investmentType: TopResultIdentifier['investment_type'],
) {
  return cn(
    'min-w-0 basis-full',
    investmentType === 'equity'
      ? 'lg:flex-1 lg:basis-[calc((100%-2.5rem)/3)]'
      : 'lg:flex-1 lg:basis-[calc(50%-0.625rem)]',
  );
}

function getPriceChangeDisplay(result: TopResult | undefined) {
  if (!result) {
    return {
      percentChange: undefined,
      valueChange: undefined,
      periodLabel: '',
    };
  }

  if (result.investment_type === 'mutual_fund') {
    return getMutualFundPriceChangeDisplay(result);
  }

  return getEquityETFPriceChangeDisplay(result);
}

function getRelatedCards(result: TopResult | undefined) {
  if (!result) {
    return [];
  }

  const cards = [
    <ArticleCard
      key="article"
      title={result.article.title}
      content={result.article.description}
      href={result.article.url}
      headingLevel="h3"
      contentLineClamp={3}
      dataTrack="search_top_result_article"
      dataTrackLabel={result.article.title}
      dataTrackContext="top_result"
    />,
  ];

  if (result.investment_type === 'equity') {
    if (result.industry) {
      cards.push(
        <CategoryCard
          key="industry"
          eyebrow="Industry"
          title={result.industry}
          description={result.sector ? `Under ${result.sector}` : undefined}
        />,
      );
    }

    if (result.market_cap_category) {
      cards.push(
        <CategoryCard
          key="market-cap-category"
          eyebrow="Market Cap Category"
          title={result.market_cap_category}
        />,
      );
    }

    return cards;
  }

  if (result.category) {
    cards.push(
      <CategoryCard
        key="fund-category"
        eyebrow="Fund Category"
        title={result.category}
      />,
    );
  }

  return cards;
}
